import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const IMAGES = [
  { url: "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?auto=format&fit=crop&w=1200&q=80", alt: "Rider on sunset highway", span: "row-span-2", tagKey: "tag1" },
  { url: "https://images.unsplash.com/photo-1599819811279-d5ad9cccf838?auto=format&fit=crop&w=900&q=80", alt: "Group of motorcyclists", span: "", tagKey: "tag2" },
  { url: "https://images.unsplash.com/photo-1547549082-6bc09f2049ae?auto=format&fit=crop&w=900&q=80", alt: "Motorcycle helmet close-up", span: "", tagKey: "tag3" },
];

export const Community = () => {
  const { t } = useLanguage();
  const QUOTES = [
    { name: "Mara K.", role: t.community.q1.role, quote: t.community.q1.text },
    { name: "Jonas R.", role: t.community.q2.role, quote: t.community.q2.text },
  ];
  return (
    <section id="community" data-testid="community-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-40 section-edge-glow pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <motion.div
            initial={{ x: -16, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease }}
            className="lg:col-span-5"
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#39FF14]/90">{t.community.eyebrow}</span>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight">{t.community.title1}<br />{t.community.title2}</h2>
            <p className="mt-4 text-white/65 text-base sm:text-lg leading-relaxed max-w-md">{t.community.subhead}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl px-4 py-3">
                <span className="font-display font-bold text-[#39FF14] text-xl">1600+</span>
                <span className="text-xs text-white/65 font-mono uppercase tracking-wider">{t.community.statRiders}</span>
              </div>
              <div className="inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl px-4 py-3">
                <span className="font-display font-bold text-white text-xl">12</span>
                <span className="text-xs text-white/65 font-mono uppercase tracking-wider">{t.community.statCountries}</span>
              </div>
            </div>

            <div className="mt-8 space-y-3">
              {QUOTES.map((q, i) => (
                <motion.div key={q.name}
                  initial={{ y: 12, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }} transition={{ duration: 0.55, ease, delay: 0.15 + i * 0.08 }}
                  data-testid="community-card"
                  className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 sm:p-5 transition-colors hover:bg-white/[0.06] hover:border-white/16"
                >
                  <Quote className="absolute top-3 right-3 w-4 h-4 text-[#39FF14]/55" />
                  <p className="text-sm text-white/85 leading-relaxed">“{q.quote}”</p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="font-display font-semibold text-white text-sm">{q.name}</span>
                    <span className="text-white/40">·</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-white/55">{q.role}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ x: 16, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="grid grid-cols-2 grid-rows-2 gap-3 sm:gap-4 h-[460px] sm:h-[520px]">
              {IMAGES.map((img) => (
                <div key={img.url} className={`relative overflow-hidden rounded-[20px] border border-white/10 bg-white/[0.04] backdrop-blur-xl ${img.span}`}>
                  <img src={img.url} alt={img.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full border border-white/12 bg-black/50 backdrop-blur-xl px-2.5 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] shadow-[0_0_10px_rgba(57,255,20,0.7)]" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/85">{t.community[img.tagKey]}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
